import { useMemo, useState } from 'react'
import { formatClock } from '@racedash/shared'
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  MultiSelect,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import {
  applyTemplate,
  emptyRegime,
  generateWindows,
  planOutsideEvent,
  planTimesProblem,
  regimeTemplates,
  templateFor,
  windowsOutsideRace,
  withStart,
  type PitWindow,
  type RacePlanDoc,
  type Regime,
  type WindowAnchor,
} from '@racedash/shared'
import type { CatalogueSession } from './api'

/**
 * Setting up a race: which rules apply, when the windows are, and who drives.
 *
 * The generator exists so nobody types eleven pairs of times, and what it
 * produces is then edited rather than regenerated — circuits publish the actual
 * schedule and it is occasionally not quite what the pattern predicts. So the
 * list below the generator is the truth, and the generator is a way of filling
 * it in.
 */
export default function RacePlanEditor({
  plan,
  sessions,
  event,
  drivers,
  onChange,
}: {
  plan: RacePlanDoc
  /** The event's timetable, where it has one. Pre-fills; never required. */
  sessions: CatalogueSession[]
  /** The event's own days, to notice a plan dated outside them. */
  event?: { name: string; startsAt: Date | null; endsAt: Date | null }
  drivers: { id: string; name: string }[]
  onChange: (next: RacePlanDoc) => void
}) {
  const [pattern, setPattern] = useState({ firstOpensAfter: 30, openFor: 20, closedFor: 20 })

  const regime = plan.regime
  const setRegime = (next: Regime) => onChange({ ...plan, regime: next })

  // The plan's own times, always. A timetable fills them in and is then out of
  // the way — most events do not have one until days beforehand, and a plan
  // that could not be made without one would be unusable exactly when it was
  // most wanted.
  const start = plan.startsAt != null ? new Date(plan.startsAt) : null
  const end = plan.endsAt != null ? new Date(plan.endsAt) : null

  const anchor = regime.windows?.anchor ?? null
  const entries = regime.windows?.entries ?? []
  const timesProblem = planTimesProblem(plan)
  const outside = new Set(windowsOutsideRace(plan))
  const wrongDays = event ? planOutsideEvent(plan, event) : false
  const template = templateFor(regime)

  /**
   * The generator works in whatever units the anchor stores.
   *
   * Keyed on the stored timestamps rather than on `start` and `end`, which are
   * fresh Date objects every render — a memo depending on those recomputed every
   * time and memoised nothing.
   */
  const { startsAt, endsAt } = plan
  const span = useMemo(() => {
    // Backwards times have no span, rather than a negative one that quietly
    // generates nothing.
    if (startsAt == null || endsAt == null || endsAt <= startsAt) return null
    if (anchor === 'timeOfDay') return { from: startsAt, to: endsAt }
    if (anchor === 'elapsed') return { from: 0, to: endsAt - startsAt }
    // A remaining anchor counts back from the end, so the span runs from minus
    // the race length to zero.
    return { from: -(endsAt - startsAt), to: 0 }
  }, [startsAt, endsAt, anchor])

  // Worked out ahead of the click, so a pattern that fits nothing between the
  // start and the end says so instead of the button doing nothing.
  const generated = useMemo(
    () =>
      span
        ? generateWindows(
            {
              firstOpensAfterMs: pattern.firstOpensAfter * 60_000,
              openForMs: pattern.openFor * 60_000,
              closedForMs: pattern.closedFor * 60_000,
            },
            span,
          )
        : [],
    [pattern, span],
  )

  const setWindows = (next: PitWindow[]) =>
    setRegime({
      ...regime,
      windows: { anchor: anchor ?? 'timeOfDay', entries: next },
    })

  /** How a stored offset reads on screen, which depends on the anchor. */
  const showOffset = (ms: number): string => {
    // With the day. "09:30" alone hid a plan saved a week early: every
    // window read correctly and every one had already closed.
    if (anchor === 'timeOfDay') {
      return `${dayOf(new Date(ms))} ${formatClock(new Date(ms))}`
    }
    const mins = Math.round(Math.abs(ms) / 60_000)
    const sign = anchor === 'remaining' ? '−' : '+'
    return `${sign}${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`
  }

  return (
    <Stack gap="xl">
      {/* -------------------------------------------------------- when */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            When
          </Title>
          <Text c="dimmed" size="sm">
            The race's own start and end. Everything else is measured from these, so they are the
            one thing worth getting right.
          </Text>
        </div>

        <Group gap="md" align="flex-end" wrap="wrap">
          <TextInput
            label="Name"
            w={160}
            value={plan.name}
            onChange={(e) => {
              const name = e.currentTarget.value
              onChange({ ...plan, name })
            }}
          />
          <TextInput
            label="Starts"
            description="Local time"
            type="datetime-local"
            w={230}
            value={toLocalInput(start)}
            onChange={(e) => {
              const startsAt = fromLocalInput(e.currentTarget.value)
              onChange(withStart(plan, startsAt))
            }}
          />
          <TextInput
            label="Ends"
            description="Local time"
            type="datetime-local"
            w={230}
            value={toLocalInput(end)}
            error={timesProblem ?? undefined}
            onChange={(e) => {
              const endsAt = fromLocalInput(e.currentTarget.value)
              onChange({ ...plan, endsAt })
            }}
          />
        </Group>

        {wrongDays && event && start && (
          <Text size="sm" c="orange.7">
            This plan starts on {dayOf(start)}, which is not one of {event.name}&apos;s days
            {event.startsAt && event.endsAt ? ` (${dayOf(event.startsAt)} to ${dayOf(event.endsAt)})` : ''}.
            Moving the start to the right day moves the windows with it.
          </Text>
        )}

        {sessions.length > 0 && (
          <Group gap="xs" wrap="wrap" align="center">
            <Text size="xs" c="dimmed">
              Or take them from the timetable:
            </Text>
            {sessions.map((s) => (
              <Button
                key={s.id}
                size="compact-xs"
                variant="subtle"
                onClick={() =>
                  onChange({
                    ...withStart(plan, s.scheduledStart?.toMillis() ?? plan.startsAt),
                    name: s.name || plan.name,
                    sessionId: s.id,
                    endsAt: s.scheduledEnd?.toMillis() ?? plan.endsAt,
                  })
                }
              >
                {s.name}
              </Button>
            ))}
          </Group>
        )}
      </Stack>

      {/* ------------------------------------------------------- the rules */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Rules
          </Title>
          <Text c="dimmed" size="sm">
            Pick the format closest to your race, then adjust the rules below. Nothing here is tied
            to a particular championship.
          </Text>
        </div>

        {/*
          A choice with a visible state rather than a row of buttons. The
          buttons applied a preset and then looked exactly as they had before,
          so there was no telling whether one was chosen, which, or whether
          they were labels. "Custom" is where the plan lands once a rule is
          changed from the preset, and is not itself something to pick —
          choosing it would have nothing to do.
        */}
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Format
          </Text>
          <Group gap="sm" wrap="wrap" align="center">
            <SegmentedControl
              value={template?.id ?? 'custom'}
              data={[
                ...regimeTemplates.map((t) => ({ value: t.id, label: t.label })),
                { value: 'custom', label: 'Custom', disabled: true },
              ]}
              onChange={(id) => {
                const t = regimeTemplates.find((x) => x.id === id)
                if (!t) return
                setRegime(applyTemplate(regime, t))
                if (t.pattern) {
                  setPattern({
                    firstOpensAfter: t.pattern.firstOpensAfterMs / 60_000,
                    openFor: t.pattern.openForMs / 60_000,
                    closedFor: t.pattern.closedForMs / 60_000,
                  })
                }
              }}
            />
            <Button
              variant="subtle"
              size="compact-sm"
              color="gray"
              onClick={() => setRegime(emptyRegime())}
            >
              Clear rules
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            {template
              ? template.description
              : 'Your own rules, set below. Choosing a format replaces them with its rules; the windows are kept where it measures them the same way.'}
          </Text>
        </Stack>

        <Group gap="md" wrap="wrap" align="flex-start">
          <NumberInput
            label="Longest stint"
            description="Minutes. Leave empty where windows already cap it."
            w={190}
            min={0}
            value={regime.stint?.maxDurationMs ? regime.stint.maxDurationMs / 60_000 : ''}
            onChange={(v) =>
              setRegime({
                ...regime,
                stint: typeof v === 'number' && v > 0 ? { maxDurationMs: v * 60_000 } : undefined,
              })
            }
          />
          <NumberInput
            label="Driver total"
            description="Minutes over the whole race."
            w={190}
            min={0}
            value={regime.driver?.maxTotalDrivingMs ? regime.driver.maxTotalDrivingMs / 60_000 : ''}
            onChange={(v) =>
              setRegime({
                ...regime,
                driver: {
                  ...regime.driver,
                  maxTotalDrivingMs: typeof v === 'number' && v > 0 ? v * 60_000 : undefined,
                },
              })
            }
          />
          <Select
            label="Driver change"
            description="Whether every stop must be one."
            w={210}
            allowDeselect={false}
            data={[
              { value: 'optional', label: 'Optional — fuel-only stops allowed' },
              { value: 'everyStop', label: 'Every stop changes driver' },
            ]}
            value={regime.stops?.driverChangeRequired ?? 'optional'}
            onChange={(v) =>
              setRegime({
                ...regime,
                stops: {
                  ...regime.stops,
                  driverChangeRequired: v === 'everyStop' ? 'everyStop' : 'optional',
                },
              })
            }
          />
        </Group>

        {regime.stops?.driverChangeRequired === 'everyStop' && (
          <Text size="xs" c="dimmed">
            Driving time can then be worked out without anybody declaring who was in the car —
            every stop is a boundary by regulation.
          </Text>
        )}
      </Stack>

      {/* ----------------------------------------------------- the windows */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Pit windows
          </Title>
          <Text c="dimmed" size="sm">
            Generate the schedule, then check it against the published times and correct anything
            that differs. The list is what counts, not the pattern.
          </Text>
        </div>

        <Select
          aria-label="Anchor"
          w={340}
          allowDeselect={false}
          data={[
            { value: 'none', label: 'No windows — stop whenever you like' },
            { value: 'timeOfDay', label: 'Times of day — a late start does not move them' },
            { value: 'elapsed', label: 'After the start — they move with the race' },
            { value: 'remaining', label: 'Before the end' },
          ]}
          value={anchor ?? 'none'}
          onChange={(v) =>
            v === 'none' || v == null
              ? setRegime({ ...regime, windows: undefined })
              : setRegime({
                  ...regime,
                  windows: { anchor: v as WindowAnchor, entries: [] },
                })
          }
        />

        {anchor === 'timeOfDay' && (
          <Text size="xs" c="dimmed">
            The choice matters when a race is stopped: times of day carry on while the race clock
            does not, so a long red flag eats a window. Anchored to the start, they move with it.
          </Text>
        )}

        {anchor && !span && (
          <Alert color="yellow" variant="light">
            {timesProblem
              ? `${timesProblem} Correct the start and end above and the schedule can be generated.`
              : "Set the race's start and end above and the schedule can be generated from a pattern."}
          </Alert>
        )}

        {anchor && span && (
          <Paper withBorder p="sm">
            <Group gap="sm" align="flex-end" wrap="wrap">
              <NumberInput
                label="First opens after"
                description="Minutes"
                w={150}
                min={0}
                value={pattern.firstOpensAfter}
                onChange={(v) =>
                  setPattern((p) => ({ ...p, firstOpensAfter: typeof v === 'number' ? v : 0 }))
                }
              />
              <NumberInput
                label="Open for"
                description="Minutes"
                w={120}
                min={1}
                value={pattern.openFor}
                onChange={(v) => setPattern((p) => ({ ...p, openFor: typeof v === 'number' ? v : 0 }))}
              />
              <NumberInput
                label="Then closed for"
                description="Minutes"
                w={140}
                min={0}
                value={pattern.closedFor}
                onChange={(v) =>
                  setPattern((p) => ({ ...p, closedFor: typeof v === 'number' ? v : 0 }))
                }
              />
              <Button
                variant="default"
                disabled={generated.length === 0}
                onClick={() => setWindows(generated)}
              >
                {entries.length ? 'Regenerate' : 'Generate'}
              </Button>
            </Group>
            {generated.length === 0 ? (
              <Text size="xs" c="red" mt="xs">
                This pattern fits no windows between the start and the end.
              </Text>
            ) : (
              <Text size="xs" c="dimmed" mt="xs">
                Makes {generated.length} window{generated.length === 1 ? '' : 's'}.
                {entries.length > 0 &&
                  ' Regenerating replaces every window below, including any you have corrected.'}
              </Text>
            )}
          </Paper>
        )}

        {anchor && entries.length > 0 && outside.size > 0 && (
          <Text size="sm" c="orange.7">
            {outside.size === entries.length ? 'Every window' : `${outside.size} of the windows`} fall
            {outside.size === 1 ? 's' : ''} outside the race&apos;s start and end. Moving the start
            to another day moves the windows with it; or regenerate them.
          </Text>
        )}

        {anchor && entries.length > 0 && (
          <Paper withBorder>
            <Table verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>#</Table.Th>
                  <Table.Th w={anchor === 'timeOfDay' ? 170 : 130}>Opens</Table.Th>
                  <Table.Th w={anchor === 'timeOfDay' ? 170 : 130}>Closes</Table.Th>
                  <Table.Th>Label</Table.Th>
                  <Table.Th w={50} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {entries.map((w, i) => (
                  <Table.Tr
                    key={i}
                    bg={outside.has(i) ? 'var(--mantine-color-orange-light)' : undefined}
                    title={outside.has(i) ? 'Outside the race’s start and end' : undefined}
                  >
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {i + 1}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {showOffset(w.opensAt)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {showOffset(w.closesAt)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder="—"
                        value={w.label ?? ''}
                        onChange={(e) => {
                          const label = e.currentTarget.value
                          setWindows(entries.map((x, n) => (n === i ? { ...x, label } : x)))
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Remove window ${i + 1}`}
                        onClick={() => setWindows(entries.filter((_, n) => n !== i))}
                      >
                        ×
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      {/* ------------------------------------------------------ the drivers */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Driver order
          </Title>
          <Text c="dimmed" size="sm">
            The intended rotation, not a schedule. Which stint each takes follows from the order
            and the stops actually made, so a safety car changing the plan does not invalidate it.
          </Text>
        </div>
        <MultiSelect
          w={420}
          placeholder={drivers.length ? 'Add drivers in order' : 'No drivers in your team list yet'}
          data={drivers.map((d) => ({ value: d.id, label: d.name }))}
          value={plan.drivers}
          onChange={(v) => onChange({ ...plan, drivers: v })}
          disabled={drivers.length === 0}
          searchable
          hidePickedOptions
        />
        {plan.drivers.length > 0 && (
          <Text size="sm">
            {plan.drivers
              .map((id, i) => `${i + 1}. ${drivers.find((d) => d.id === id)?.name ?? 'Unknown'}`)
              .join('   ')}
          </Text>
        )}
      </Stack>
    </Stack>
  )
}

/** A Date as an <input type="datetime-local"> value, in the viewer's own zone. */
function toLocalInput(d: Date | null): string {
  if (!d) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromLocalInput(v: string): number | null {
  if (!v) return null
  const t = new Date(v).getTime()
  return Number.isFinite(t) ? t : null
}

/** A day as the editor names it: "Sun 4 Oct". */
function dayOf(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}
